import { Link, useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { useFrontendAuth } from '../hooks/useAuth';
import { useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import { Heart, ChevronLeft, ChevronRight, ChevronDown, ShoppingCart, ImageOff, Clock } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { getFavourites, addFavourite, removeFavourite } from '../services/api.shop';
import SellerInfoPopover from './SellerInfoPopover';
import SellerRatingStars from './SellerRatingStars';
import { useTranslation } from '../context/TranslationContext';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import { translateWithFallback } from '../utils/translateWithFallback';
import { formatMoney } from '../utils/productValidation';
import { translateProductCondition } from '../utils/productConditionLabel';
import { formatPublicHandle } from '../utils/usernameRules';
import {
    createHoldCountdownAnchor,
    formatCountdown,
    isReservationActive,
    remainingFromAnchor,
} from '../utils/reservationCountdown';

interface ProductCardProps {
    product: Product;
    showPrice: boolean;
    hideOwnBadge: boolean;
    favourites?: Product[];
    onBuyClick?: () => void;
    /**
     * When set (e.g. buy from My Journey recommendations), the order is linked to this child
     * in the background for outgrown / journey features. Not shown as a UI step.
     */
    childId?: number | null;
    /** Briefly accent the card after a successful listing. */
    highlight?: boolean;
}

const FAVOURITES_QUERY_KEY = ['favourites'] as const;

/** Empty / failed photo — same treatment on cards and product details. */
export function ProductImageFallback({
    title,
    label,
    iconClassName = 'h-10 w-10',
}: {
    title?: string;
    label: string;
    iconClassName?: string;
}) {
    return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center">
            <ImageOff
                className={`${iconClassName} text-[color-mix(in_srgb,var(--text-color),transparent_55%)]`}
                strokeWidth={1.5}
            />
            <p className="line-clamp-3 text-sm font-medium text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                {title || label}
            </p>
        </div>
    );
}

const ProductCard: React.FC<ProductCardProps> = ({
    product,
    showPrice,
    hideOwnBadge,
    favourites: favouritesProp,
    onBuyClick,
    childId,
    highlight,
}) => {
    const { user } = useFrontendAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const navigate = useNavigate();

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovering, setIsHovering] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);
    const [nowMs, setNowMs] = useState(() => Date.now());

    const rawImages = (product.imageUrls ?? []).filter((u) => !!u && u.trim().length > 0);
    const hasImages = rawImages.length > 0 && !imageFailed;
    const images = hasImages ? rawImages : [];
    const currentImage = hasImages ? images[currentImageIndex] : null;

    useEffect(() => {
        setImageFailed(false);
        setCurrentImageIndex(0);
    }, [product.id]);

    const productHref = `/products/${product.id}`;

    const isOwnedByCurrentUser = !!user && user.id === product.sellerId;

    const holdAnchor = useMemo(
        () =>
            createHoldCountdownAnchor(
                product.holdRemainingSeconds,
                product.reservedUntil
            ),
        [product.holdRemainingSeconds, product.reservedUntil]
    );
    const reservationLive = isReservationActive(
        product.isReserved,
        product.reservedUntil,
        nowMs,
        product.holdRemainingSeconds
    );
    const reservedByMe = !!product.reservedByMe && reservationLive;
    const holdBlocksBuy = reservationLive && !reservedByMe;
    const countdownLabel = formatCountdown(remainingFromAnchor(holdAnchor, nowMs));

    useEffect(() => {
        if (!reservationLive && !product.isReserved) return;
        const id = window.setInterval(() => setNowMs(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [reservationLive, product.isReserved, product.reservedUntil, product.holdRemainingSeconds]);

    // Prefer parent-provided favourites (Home/Search) so N cards do not each wire a query.
    // React Query still dedupes, but skipping the hook work when prop is passed is cheaper.
    const parentProvidesFavourites = favouritesProp !== undefined;
    const { data: favouritesFromQuery = [] } = useProtectedQuery({
        queryKey: FAVOURITES_QUERY_KEY,
        queryFn: getFavourites,
        staleTime: 10 * 60 * 1000,
        enabled: !parentProvidesFavourites && !!user,
    });

    const favouritesList = parentProvidesFavourites ? favouritesProp! : favouritesFromQuery;
    const isFavorited = favouritesList.some((f) => f.id === product.id);

    useEffect(() => {
        setImageFailed(false);
        setCurrentImageIndex(0);
    }, [product.id, product.imageUrls]);

    useEffect(() => {
        if (!isHovering || images.length <= 1) return;
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3500);
        return () => clearInterval(interval);
    }, [isHovering, images.length]);

    const snapshotFavourites = () =>
        queryClient.getQueriesData<Product[]>({ queryKey: FAVOURITES_QUERY_KEY });

    const restoreFavourites = (previous: [QueryKey, Product[] | undefined][]) => {
        previous.forEach(([key, data]) => {
            queryClient.setQueryData(key, data);
        });
    };

    const addMutation = useMutation({
        mutationFn: () => addFavourite(product.id),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: FAVOURITES_QUERY_KEY });
            const previous = snapshotFavourites();
            queryClient.setQueriesData<Product[]>({ queryKey: FAVOURITES_QUERY_KEY }, (old) => {
                if (!old) return [product];
                if (old.some((p) => p.id === product.id)) return old;
                return [...old, product];
            });
            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous) restoreFavourites(context.previous);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: FAVOURITES_QUERY_KEY });
        },
    });

    const removeMutation = useMutation({
        mutationFn: () => removeFavourite(product.id),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: FAVOURITES_QUERY_KEY });
            const previous = snapshotFavourites();
            queryClient.setQueriesData<Product[]>({ queryKey: FAVOURITES_QUERY_KEY }, (old) =>
                (old ?? []).filter((p) => p.id !== product.id)
            );
            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous) restoreFavourites(context.previous);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: FAVOURITES_QUERY_KEY });
        },
    });

    const stop = (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleToggleFavourite = (e: React.MouseEvent) => {
        stop(e);
        if (!user || isOwnedByCurrentUser) return;
        if (isFavorited) removeMutation.mutate();
        else addMutation.mutate();
    };

    const goToPrev = (e: React.MouseEvent) => {
        stop(e);
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const goToNext = (e: React.MouseEvent) => {
        stop(e);
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const isLoading = addMutation.isPending || removeMutation.isPending;

    const goToProduct = () => navigate(productHref);

    const handleCardClick = (e: React.MouseEvent) => {
        // Ignore if user selected text or clicked an interactive descendant (those stopPropagation)
        const target = e.target as HTMLElement;
        if (target.closest('a, button, [data-no-card-nav]')) return;
        goToProduct();
    };

    const handleCardKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            const target = e.target as HTMLElement;
            if (target.closest('a, button, [data-no-card-nav]')) return;
            e.preventDefault();
            goToProduct();
        }
    };

    const handleBuyClick = (e: React.MouseEvent) => {
        stop(e);
        if (holdBlocksBuy) return;
        if (onBuyClick) {
            onBuyClick();
            return;
        }
        if (isOwnedByCurrentUser) return;

        // Shared branded handoff on /checkout (reservation + Shopify redirect live there)
        const childQs =
            childId != null && childId > 0 ? `?childId=${encodeURIComponent(String(childId))}` : '';
        const checkoutPath = `/checkout/${product.id}${childQs}`;
        if (!user) {
            navigate(`/login?returnUrl=${encodeURIComponent(checkoutPath)}`);
            return;
        }
        navigate(checkoutPath);
    };

    const showRating =
        (product.averageSellerRating ?? 0) > 0 && (product.sellerRatingCount ?? 0) > 0;
    const sellerName = formatPublicHandle(
        product.sellerFirstName,
        tr('product.details.seller', 'Seller')
    );
    const sellerInitials = (sellerName.replace(/^@/, '').charAt(0) || '?').toUpperCase();

    /** Two-row seller band: row1 person control, row2 trust line (always same min-height). */
    const sellerHeaderBody = (
        <span className="flex w-full min-w-0 flex-col items-start gap-0.5 text-left">
            <span className="flex w-full min-w-0 items-center gap-1.5">
                <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--accent-color)] text-[11px] font-semibold leading-none text-white"
                    aria-hidden
                >
                    {sellerInitials}
                </span>
                {/* flex-1 + truncate: short names (Jarno) use natural width; long ones ellipsize.
                    Region is hard-capped so it never steals the name’s room (esp. mobile). */}
                <span
                    className="min-w-0 flex-1 truncate whitespace-nowrap text-sm font-semibold text-[var(--accent-color)]"
                    title={sellerName}
                >
                    {sellerName}
                </span>
                {product.sellerId ? (
                    <ChevronDown
                        className="h-3.5 w-3.5 shrink-0 text-[color-mix(in_srgb,var(--text-color),transparent_45%)]"
                        aria-hidden
                        strokeWidth={2.25}
                    />
                ) : null}
            </span>
            {/* Single-line trust row — never wraps (NL “Nieuw op Petito” was stacking two lines) */}
            <span className="flex h-5 w-full min-w-0 items-center overflow-hidden">
                {showRating ? (
                    <SellerRatingStars
                        averageRating={product.averageSellerRating}
                        ratingCount={product.sellerRatingCount}
                        size="sm"
                    />
                ) : (
                    <span className="truncate whitespace-nowrap text-xs leading-none text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                        {tr('seller.new.label', 'New on Petito')}
                    </span>
                )}
            </span>
        </span>
    );

    return (
        <div
            role="link"
            tabIndex={0}
            onClick={handleCardClick}
            onKeyDown={handleCardKeyDown}
            className={`group flex h-full w-full min-w-0 cursor-pointer flex-col overflow-visible rounded-2xl border bg-white shadow-sm transition-all duration-300 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-color)]/50 ${
                highlight
                    ? 'border-[var(--highlight-color,#5A8A62)] ring-4 ring-[var(--highlight-color,#5A8A62)]/40 animate-pulse'
                    : 'border-[var(--accent-color)]/30'
            }`}
            data-product-id={product.id}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            {/* Seller header — interactive person control (popover) + region metadata top-right */}
            <div
                data-no-card-nav
                className="relative z-10 flex items-start gap-1.5 rounded-t-2xl border-b border-[color-mix(in_srgb,var(--accent-color),transparent_70%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] pl-2 pr-2.5 pt-1.5 pb-1.5 sm:gap-2 sm:pl-2.5 sm:pr-3"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="min-w-0 flex-1 overflow-hidden">
                    {product.sellerId ? (
                        <SellerInfoPopover
                            sellerId={product.sellerId}
                            sellerName={sellerName}
                            region={product.sellerRegion}
                            averageRating={product.averageSellerRating}
                            ratingCount={product.sellerRatingCount}
                        >
                            {sellerHeaderBody}
                        </SellerInfoPopover>
                    ) : (
                        sellerHeaderBody
                    )}
                </div>
                {/* Region yields first when space is tight (name stays preferred) */}
                {product.sellerRegion ? (
                    <span
                        className="min-w-0 max-w-[4.5rem] shrink truncate pt-1.5 text-right text-xs leading-snug text-[color-mix(in_srgb,var(--text-color),transparent_40%)] sm:max-w-[5.5rem] lg:max-w-[7rem]"
                        title={product.sellerRegion}
                    >
                        {product.sellerRegion}
                    </span>
                ) : null}
            </div>

            {/* Image area */}
            <div className="relative aspect-square overflow-hidden bg-[color-mix(in_srgb,var(--bg-color),white_10%)]">
                {hasImages && currentImage ? (
                    <img
                        src={currentImage}
                        alt={product.title}
                        loading="lazy"
                        decoding="async"
                        onError={() => setImageFailed(true)}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                ) : (
                    <ProductImageFallback
                        title={product.title}
                        label={tr('component.productcard.noimage', 'No image')}
                    />
                )}

                {/* Condition — top left (same filter.condition.* keys as sidebar) */}
                {product.condition && (
                    <div className="pointer-events-none absolute top-3 left-3 z-20 flex flex-col gap-1">
                        <span className="rounded-full border border-[var(--accent-color)]/20 bg-white/95 px-3 py-1 text-xs text-[var(--text-color)] shadow-sm backdrop-blur-sm">
                            {translateProductCondition(tr, product.condition)}
                        </span>
                        {product.allowsLocalPickup ? (
                            <span className="rounded-full border border-[var(--accent-color)]/20 bg-white/95 px-3 py-1 text-xs text-[var(--text-color)] shadow-sm backdrop-blur-sm">
                                {tr('product.pickup.badge', 'Open to local pickup')}
                            </span>
                        ) : null}
                    </div>
                )}

                {/* Favourite — top right (never on own listings) */}
                {user && !isOwnedByCurrentUser && (
                    <button
                        type="button"
                        data-no-card-nav
                        onClick={handleToggleFavourite}
                        disabled={isLoading}
                        className="absolute top-3 right-3 z-20 min-h-11 min-w-11 rounded-full bg-white/95 p-2.5 shadow-sm backdrop-blur-sm transition-colors hover:bg-white inline-flex items-center justify-center"
                        aria-label="Toggle favourite"
                    >
                        <Heart
                            className={`h-4 w-4 transition-colors ${
                                isFavorited
                                    ? 'fill-[var(--accent-color)] text-[var(--accent-color)]'
                                    : 'text-[var(--text-color)]/50'
                            }`}
                        />
                    </button>
                )}

                {/* Own listing — bottom left over image */}
                {!hideOwnBadge && isOwnedByCurrentUser && (
                    <div className="pointer-events-none absolute bottom-3 left-3 z-20 max-w-[70%]">
                        <span className="inline-block truncate rounded-full border border-[var(--accent-color)] bg-[var(--accent-color)]/95 px-2.5 py-1 text-[10px] font-medium text-white shadow-sm">
                            {tr('component.productcard.ownBadge', 'Your listing')}
                        </span>
                    </div>
                )}

                {/* Carousel */}
                {hasImages && images.length > 1 && (
                    <>
                        <button
                            type="button"
                            data-no-card-nav
                            onClick={goToPrev}
                            className="carousel-ctrl absolute left-2 top-1/2 z-20 -translate-y-1/2 min-h-11 min-w-11 rounded-full bg-black/60 p-2 text-white transition-all hover:bg-black/80"
                            aria-label="Previous image"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            data-no-card-nav
                            onClick={goToNext}
                            className="carousel-ctrl absolute right-2 top-1/2 z-20 -translate-y-1/2 min-h-11 min-w-11 rounded-full bg-black/60 p-2 text-white transition-all hover:bg-black/80"
                            aria-label="Next image"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                        <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1">
                            {images.map((_, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    data-no-card-nav
                                    onClick={(e) => {
                                        stop(e);
                                        setCurrentImageIndex(idx);
                                    }}
                                    className={`h-1.5 w-1.5 rounded-full transition-all ${
                                        idx === currentImageIndex ? 'scale-125 bg-white' : 'bg-white/60'
                                    }`}
                                    aria-label={`Image ${idx + 1}`}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col p-4">
                <h3 className="mb-2 line-clamp-2 text-[15px] leading-snug text-[var(--text-color)] transition-colors group-hover:text-[var(--accent-color)]">
                    {product.title}
                </h3>

                {showPrice ? (
                    <div className="mb-3 flex items-baseline gap-2">
                        <span className="text-2xl text-[var(--accent-color)]">
                            €{formatMoney(product.price)}
                        </span>
                    </div>
                ) : (
                    <p className="mb-3 text-sm italic text-[var(--text-color)]/60">
                        {t('component.productcard.loginToSeePrice')}
                    </p>
                )}

                {showPrice && !isOwnedByCurrentUser && (
                    <div className="mt-auto flex flex-col gap-1.5">
                        {holdBlocksBuy ? (
                            <button
                                type="button"
                                data-no-card-nav
                                disabled
                                title={tr(
                                    'checkout.reserved.hint',
                                    'Someone is completing checkout. This item frees if they do not pay in time.'
                                )}
                                className="flex min-h-11 w-full cursor-not-allowed items-center justify-center gap-2 rounded-full border border-[var(--accent-color)]/40 bg-[color-mix(in_srgb,var(--bg-color),white_30%)] py-2.5 text-[var(--text-color)] opacity-90"
                            >
                                <Clock className="h-4 w-4 shrink-0 text-[var(--accent-color)]" />
                                <span className="text-sm font-medium">
                                    {tr('checkout.reserved', 'Reserved')}
                                    <span className="ml-1.5 tabular-nums text-[color-mix(in_srgb,var(--text-color),transparent_25%)]">
                                        {countdownLabel}
                                    </span>
                                </span>
                            </button>
                        ) : (
                            <button
                                type="button"
                                data-no-card-nav
                                onClick={handleBuyClick}
                                className="flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-[var(--accent-color)] py-2.5 text-white transition-all hover:brightness-95"
                            >
                                <ShoppingCart className="h-4 w-4" />
                                <span className="text-sm">
                                    {reservedByMe
                                        ? tr('checkout.reserved.continue', 'Continue checkout')
                                        : t('product.details.buynow') || 'Buy'}
                                </span>
                            </button>
                        )}
                        {reservationLive && (
                            <p className="text-center text-[11px] leading-snug text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {reservedByMe
                                    ? tr(
                                          'checkout.reserved.continue.hint',
                                          'You started checkout — finish payment or wait for the hold to end.'
                                      )
                                    : `${tr('checkout.reserved.until', 'Released in')} ${countdownLabel}`}
                            </p>
                        )}
                    </div>
                )}

                {showPrice && isOwnedByCurrentUser && (
                    <Link
                        to={`/edit-product/${product.id}`}
                        data-no-card-nav
                        onClick={(e) => e.stopPropagation()}
                        className="mt-auto flex w-full items-center justify-center rounded-full border border-[var(--accent-color)] py-2.5 text-sm font-medium text-[var(--text-color)] no-underline transition hover:bg-[var(--accent-color)]/15"
                    >
                        {tr('component.productcard.edit', 'Edit listing')}
                    </Link>
                )}
            </div>
        </div>
    );
};

export default ProductCard;
